import { AppEnvironment } from "@/client/core/Core.api";

export const kSidepanelHostParam = "spliceHost";
export const kSidepanelHostValue = "sidepanel";
const kSidepanelStorageKey = "labs-sidepanel-host";

const readSidepanelFlag = (): boolean => {
    try {
        return window.sessionStorage.getItem(kSidepanelStorageKey) === "true";
    } catch {
        return false;
    }
};

const storeSidepanelFlag = (): void => {
    try {
        window.sessionStorage.setItem(kSidepanelStorageKey, "true");
    } catch {
        // Storage unavailable — the flag survives only this page.
    }
};

/**
 * True when embedded by a host that only provides the auth-token bridge (the
 * Splice Sounds plugin's Labs sidepanel). The host appends a query param to
 * the iframe URL; because in-app navigation uses full page loads that drop
 * query params, the flag is also persisted in sessionStorage.
 *
 * sessionStorage is a cache here, not the carrier. A cross-origin iframe can
 * have its storage partitioned or blocked outright, and both accessors below
 * swallow that and report `false` — which silently drops the app into plugin
 * mode, where it waits forever for resource messages the sidepanel never
 * answers. The marker therefore rides the URL across in-app navigation too;
 * see `client/utils/sidepanel-host-marker.ts`.
 *
 * Such hosts do not answer resource or audio messages, so the app must run
 * the website experience inside the iframe.
 */
export const isSidepanelHost = (): boolean => {
    if (typeof window === "undefined" || window.parent === window) {
        return false;
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get(kSidepanelHostParam) === kSidepanelHostValue) {
        storeSidepanelFlag();
        return true;
    }
    return readSidepanelFlag();
};

export const getAppEnvironment = (): AppEnvironment => {
    if (typeof window === "undefined" || window.parent === window) {
        return AppEnvironment.Website;
    }
    return isSidepanelHost() ? AppEnvironment.Website : AppEnvironment.Plugin;
};
